/**
 * Logística → Notas de Crédito (`/logistica/notas-credito`).
 *
 * Puerto de `Logistics::notas_credito` + `views/documents/notas_credito.php`: la
 * consulta de las notas de crédito emitidas, con la misma tabla (fecha, documento,
 * cliente, comprobante afectado, estado, motivo, referencia y acciones), sus filtros
 * (fechas del mes en curso, serie, número y estado) y las descargas de PDF, XML y CDR.
 * El PDF oficial viene del servicio de CPE; además, el panel arma su propio PDF de la
 * nota para revisarla o imprimirla sin salir de aquí.
 *
 * **Emitir una nota de crédito** (`Logistics::crear_nota_credito`) crea el
 * comprobante y lo envía al servicio externo de CPE, así que sigue pendiente y la
 * pantalla lo explica con un aviso.
 */

import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  FileArchive,
  FileCode,
  FileText,
  Filter,
  Printer,
  RotateCcw,
  ShieldAlert,
  Undo2,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { CampoFecha } from '@/components/ui/calendario';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatNumber } from '@/lib/utils';

import { useNotasCredito } from '../notas-credito.api';
import type { NotasCreditoFiltros } from '../notas-credito.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const POR_PAGINA = 10;

/** Fecha local en `AAAA-MM-DD` (no usar `toISOString`, que cambia de día). */
function fechaLocal(fecha: Date): string {
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');

  return `${fecha.getFullYear()}-${mes}-${dia}`;
}

/** Rango por defecto: el mes en curso, como los campos del panel anterior. */
function rangoPorDefecto(): { desde: string; hasta: string } {
  const hoy = new Date();

  return {
    desde: fechaLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1)),
    hasta: fechaLocal(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0)),
  };
}

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Botón de descarga de un documento de la nota. */
function Descarga({
  titulo,
  href,
  icono: Icono,
}: {
  titulo: string;
  href: string;
  icono: typeof FileText;
}) {
  if (!href) {
    return (
      <Button variant="ghost" size="icon" title={`${titulo} · no disponible`} disabled>
        <Icono className="size-4 text-ink-300" aria-hidden="true" />
      </Button>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={titulo}
      className="inline-flex size-10 items-center justify-center rounded-control text-ink-600 transition-colors hover:bg-ink-100 hover:text-ink-900"
    >
      <Icono className="size-4" aria-hidden="true" />
    </a>
  );
}

export function NotasCreditoPage() {
  useDocumentTitle('Logística · Notas de Crédito');

  const inicial = rangoPorDefecto();
  const [desde, setDesde] = useState(inicial.desde);
  const [hasta, setHasta] = useState(inicial.hasta);
  const [serie, setSerie] = useState('');
  const [numero, setNumero] = useState('');
  const [estado, setEstado] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [filtros, setFiltros] = useState<NotasCreditoFiltros>({
    desde: inicial.desde,
    hasta: inicial.hasta,
    perPage: POR_PAGINA,
    page: 1,
  });
  const [imprimiendo, setImprimiendo] = useState<number | null>(null);
  const [errorImpresion, setErrorImpresion] = useState<string | null>(null);

  const notas = useNotasCredito(filtros);

  /** Abre el PDF de la nota que arma el propio panel. */
  async function imprimirPdf(notaId: number) {
    setErrorImpresion(null);
    setImprimiendo(notaId);

    try {
      await abrirPdfAutenticado(`/api/logistica/notas-credito/${notaId}/pdf`);
    } catch (error) {
      setErrorImpresion(mensajeDe(error, 'No se pudo generar la nota de crédito.'));
    } finally {
      setImprimiendo(null);
    }
  }

  const sinPermiso = notas.error instanceof ApiError && notas.error.status === 403;
  const lista = notas.data?.notas ?? [];
  const total = notas.data?.total ?? 0;
  const pagina = notas.data?.page ?? 1;
  const paginas = notas.data?.paginas ?? 1;
  const estados = notas.data?.estados ?? [];
  const desdeFila = total === 0 ? 0 : (pagina - 1) * POR_PAGINA + 1;
  const hastaFila = Math.min(pagina * POR_PAGINA, total);

  /** Aplica los filtros y vuelve a la primera página. */
  function aplicar() {
    setFiltros({
      desde: desde || undefined,
      hasta: hasta || undefined,
      serie: serie.trim() || undefined,
      numero: numero.trim() || undefined,
      estado: estado || undefined,
      search: busqueda.trim() || undefined,
      perPage: POR_PAGINA,
      page: 1,
    });
  }

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/logistica"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Logística
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Undo2 className="size-6 text-brand-500" aria-hidden="true" />
          Notas de Crédito
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Notas de crédito emitidas, con el comprobante que afectan, su estado y las descargas de
          PDF, XML y CDR.
        </p>
      </div>

      {notas.data ? <Badge variant="outline">{formatNumber(total)} notas</Badge> : null}
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            {mensajeDe(notas.error, 'Esta pantalla es del grupo logistics.')}
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cabecera}

      <Alert variant="info" title="Acciones en curso de migración">
        La <strong>consulta</strong> de las notas y las descargas de PDF, XML y CDR ya están
        disponibles. <strong>Emitir una nota de crédito</strong> crea el comprobante y lo envía al
        servicio externo de CPE, así que se portará con ese circuito (por ahora se hace desde el panel
        anterior).
      </Alert>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="nc-desde" className="text-sm font-medium text-ink-700">
                Fecha inicio
              </label>
              <CampoFecha
                id="nc-desde"
               
                className={cn(CONTROL, 'w-40')}
                value={desde}
                onChange={(evento) => setDesde(evento.target.value)}
               tipo="date" />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="nc-hasta" className="text-sm font-medium text-ink-700">
                Fecha fin
              </label>
              <CampoFecha
                id="nc-hasta"
               
                className={cn(CONTROL, 'w-40')}
                value={hasta}
                onChange={(evento) => setHasta(evento.target.value)}
               tipo="date" />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="nc-serie" className="text-sm font-medium text-ink-700">
                Serie NC
              </label>
              <input
                id="nc-serie"
                className={cn(CONTROL, 'w-32')}
                placeholder="FC01"
                value={serie}
                onChange={(evento) => setSerie(evento.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="nc-numero" className="text-sm font-medium text-ink-700">
                Nro. NC
              </label>
              <input
                id="nc-numero"
                className={cn(CONTROL, 'w-28')}
                placeholder="1"
                value={numero}
                onChange={(evento) => setNumero(evento.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="nc-estado" className="text-sm font-medium text-ink-700">
                Estado API
              </label>
              <Select
                id="nc-estado"
                className={cn(CONTROL, 'w-44')}
                value={estado}
                onChange={(evento) => setEstado(evento.target.value)}
              >
                <option value="">Todos</option>
                {estados.map((opcion) => (
                  <option key={opcion} value={opcion}>
                    {opcion}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="nc-buscar" className="text-sm font-medium text-ink-700">
                Búsqueda libre
              </label>
              <input
                id="nc-buscar"
                className={cn(CONTROL, 'w-48')}
                placeholder="Cliente, RUC, motivo…"
                value={busqueda}
                onChange={(evento) => setBusqueda(evento.target.value)}
              />
            </div>

            <Button
              size="sm"
              leftIcon={<Filter className="size-3.5" aria-hidden="true" />}
              onClick={aplicar}
            >
              Filtrar
            </Button>

            <Button
              variant="outline"
              size="sm"
              leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
              onClick={() => {
                setDesde('');
                setHasta('');
                setSerie('');
                setNumero('');
                setEstado('');
                setBusqueda('');
                setFiltros({ perPage: POR_PAGINA, page: 1 });
              }}
            >
              Limpiar
            </Button>
          </div>

          {errorImpresion ? (
            <Alert variant="error" title="No se pudo generar la nota">
              {errorImpresion}
            </Alert>
          ) : null}

          {notas.isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : notas.error ? (
            <Alert variant="error" title="No se pudieron leer las notas de crédito">
              {mensajeDe(notas.error, 'La API no respondió.')}
            </Alert>
          ) : lista.length === 0 ? (
            <Alert variant="info" title="Sin resultados">
              No hay notas de crédito con esos filtros.
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    <th className="py-2">Fecha</th>
                    <th className="py-2">Documento</th>
                    <th className="py-2">Cliente</th>
                    <th className="py-2">Afectado</th>
                    <th className="py-2">Estado</th>
                    <th className="py-2">Motivo</th>
                    <th className="py-2">Referencia</th>
                    <th className="py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((nota) => (
                    <tr key={nota.id} className="border-b border-ink-50 last:border-0">
                      <td className="py-3 text-ink-700">
                        <span className="block text-xs">{nota.fecha ?? '—'}</span>
                        {nota.fecha_nota ? (
                          <span className="block text-[11px] text-ink-400">
                            NC: {nota.fecha_nota}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-3">
                        <span className="block font-semibold text-brand-600">{nota.nota}</span>
                        <span className="block text-[11px] text-ink-400">
                          Registro: {nota.id}
                          {nota.codigo ? ` · ${nota.codigo}` : ''}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className="block font-semibold text-ink-900">
                          {nota.cliente || '—'}
                        </span>
                        <span className="block text-[11px] text-ink-400">
                          {nota.ruc || 'sin RUC'}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className="block text-ink-700">{nota.documento_afectado}</span>
                        <span className="block text-[11px] text-ink-400">{nota.tipo_afectado}</span>
                      </td>
                      <td className="py-3">
                        <Badge variant={nota.variante_estado}>{nota.estado_api || '—'}</Badge>
                        {nota.envio && nota.envio !== 'pendiente' ? (
                          <span className="mt-1 block text-[11px] text-ink-400">{nota.envio}</span>
                        ) : null}
                      </td>
                      <td className="max-w-[220px] py-3 text-ink-600">
                        {nota.motivo || <span className="text-ink-300">—</span>}
                      </td>
                      <td className="max-w-[160px] py-3">
                        <span className="block truncate text-[11px] text-ink-400">
                          {nota.referencia || '—'}
                        </span>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-0.5">
                          <Button
                            variant="ghost"
                            size="icon"
                            title="PDF elaborado por el panel"
                            aria-label={`PDF de la nota de crédito ${nota.id}`}
                            loading={imprimiendo === nota.id}
                            onClick={() => void imprimirPdf(nota.id)}
                          >
                            <Printer className="size-4 text-ink-600" aria-hidden="true" />
                          </Button>
                          <Descarga
                            titulo="PDF de la nota de crédito"
                            href={nota.pdf_link}
                            icono={FileText}
                          />
                          <Descarga
                            titulo="XML de la nota de crédito"
                            href={nota.xml_link}
                            icono={FileCode}
                          />
                          <Descarga
                            titulo="CDR de la nota de crédito"
                            href={nota.cdr_link}
                            icono={FileArchive}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-ink-400">
                Mostrando {formatNumber(desdeFila)}–{formatNumber(hastaFila)} de{' '}
                {formatNumber(total)} notas · página {pagina} de {formatNumber(paginas)}
              </p>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<ChevronLeft className="size-3.5" aria-hidden="true" />}
                  disabled={pagina <= 1}
                  onClick={() => setFiltros({ ...filtros, page: Math.max(1, pagina - 1) })}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  rightIcon={<ChevronRight className="size-3.5" aria-hidden="true" />}
                  disabled={pagina >= paginas}
                  onClick={() => setFiltros({ ...filtros, page: pagina + 1 })}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
